import {
  BehaviorSubject,
  catchError,
  delay,
  filter,
  from,
  mergeMap,
  Observable,
  of,
} from 'rxjs';
import { Dependencies } from '../../../../configurations/dependencies/dependencies.interface';
import { Action, AnyAction } from '../../../state/action.type';
import { AppState } from '../../../state/app.state';
import {
  emailSignUp,
  signInSuccess,
  signUpFailure,
  verifyToken,
} from '../../../state/authentication/authentication.action';
import {
  EmailSignUp,
  EMAIL_SIGN_UP,
} from '../../../state/authentication/authentication.action.type';
import { createUserProfile } from '../../../state/user/user.action';
import { AUTH_USECASE_ERROR_MESSAGES } from '../../errors/usecases.errorMessages/authenticationUsecase.error.messages';
import { SignUpAuthResponse } from '../../models/authenticator.model'; // Use existing model
import { SignUpProfile } from '../../models/user.model';

const DEFAULT_RETRY_DELAY = 300;

export const emailSignUpUsecase = (
  action$: Observable<Action>,
  _state$: BehaviorSubject<AppState>,
  dependencies: Dependencies,
): Observable<Action> =>
  action$.pipe(
    filter(
      (action: AnyAction): action is EmailSignUp =>
        action.type === EMAIL_SIGN_UP,
    ),
    // First mergeMap: Validation
    mergeMap((action: EmailSignUp) => {
      // Validation checks
      if (
        !action.payload.firstName?.trim() ||
        !action.payload.lastName?.trim()
      ) {
        return of(signUpFailure('First and last name are required'));
      }

      if (action.payload.password !== action.payload.confirmPassword) {
        return of(signUpFailure("Passwords don't match"));
      }

      if (action.payload.password.length < 6) {
        return of(signUpFailure('Password must be at least 6 characters long'));
      }

      // If validation passes, continue with signup
      return from(
        // for authenticator gateway, only email and password are required
        dependencies.oicdAuthenticatorGateway.emailSignUp(
          action.payload.email,
          action.payload.password,
        ),
      ).pipe(
        // Second mergeMap: Handle authentication response
        mergeMap((response: SignUpAuthResponse) => {
          const userData: SignUpProfile = {
            firstName: action.payload.firstName,
            lastName: action.payload.lastName,
            email: response.email,
            nationality: action.payload.nationality || undefined,
            dateOfBirth: action.payload.dateOfBirth || undefined,
            gender: action.payload.gender || undefined,
          };

          return [
            verifyToken({ token: response.token, retry: 2 }),
            signInSuccess(response.token),
            createUserProfile({
              token: response.token,
              profile: userData,
              retry: 0,
            }),
          ];
        }),
        catchError((error: Error) => {
          console.warn(error.message);
          const remainingRetries = action.payload?.retry
            ? action.payload.retry - 1
            : 0;
          if (remainingRetries) {
            return of(
              emailSignUp({ ...action.payload, retry: remainingRetries }),
            ).pipe(delay(DEFAULT_RETRY_DELAY));
          }
          return of(signUpFailure(AUTH_USECASE_ERROR_MESSAGES.SIGN_UP_FAILED));
        }),
      );
    }),
  );
